import * as React from "react";
import { Box, Typography, TextField, InputLabel, Link, Stack, Button, Chip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GeneralModal from "../modals/GeneralModal";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useFlash } from "../FlashContext";
import { PendingReviewCandidate } from "../types/pendingReview";


interface SearchResult {
  patient_id: number;
  mrn: string;
  full_name: string;
  date_of_birth: string | null;
}

type ModalStep = "search" | "found" | "not_found";

interface Props {
  open: boolean;
  reviewId: number | null;
  candidates?: PendingReviewCandidate[];
  onClose: () => void;
  onSuccess: (reviewId: number) => void;
}


const labelStyle: React.CSSProperties = {
  margin: 0,
  color: "var(--med-grey, #4A4442)",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontWeight: 500,
};

const valueStyle: React.CSSProperties = {
  margin: "2px 0 0",
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontSize: "14px",
  fontWeight: 700,
};


const UpdatePendingReviewModal: React.FC<Props> = ({
  open,
  reviewId,
  candidates = [],
  onClose,
  onSuccess,
}) => {
  const authCtx = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [step, setStep] = React.useState<ModalStep>("search");
  const [mrn, setMrn] = React.useState<string>("");
  const [searchedMrn, setSearchedMrn] = React.useState<string>("");
  const [searchResult, setSearchResult] = React.useState<SearchResult | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (open) {
      setStep("search");
      setMrn("");
      setSearchedMrn("");
      setSearchResult(null);
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };


  const handleSearch = async () => {
    const trimmed = mrn.trim();
    if (!trimmed) {
      showToast({ title: "MRN Required", text: "Please enter an MRN to search.", type: "error" });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `/data_fetching/manual_eligibility_reviews/search_by_mrn?mrn=${encodeURIComponent(trimmed)}`,
        { method: "GET", headers: getHeaders(authCtx.csrfToken) }
      );
      const result = await res.json();

      if (!result.success) {
        showToast({ title: "Search Failed", text: result.error || "Unable to search for patient.", type: "error" });
        return;
      }

      setSearchedMrn(result.searched_mrn || trimmed);

      if (result.found) {
        setSearchResult(result.resource);
        setStep("found");
      } else {
        setStep("not_found");
      }
    } catch {
      showToast({ title: "Search Failed", text: "Unable to search for patient.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    setSearchResult(null);
    setStep("search");
  };

  const handleSelectCandidate = (candidate: PendingReviewCandidate) => {
    setSearchResult({
      patient_id:    candidate.patient_id,
      mrn:           candidate.mrn || "—",
      full_name:     candidate.name || "—",
      date_of_birth: candidate.date_of_birth,
    });
    setStep("found");
  };


  const handleUpdate = async () => {
    if (!reviewId || !searchResult) return;

    setLoading(true);
    try {
      const res = await fetch(
        `/data_fetching/manual_eligibility_reviews/${reviewId}/apply_to_patient`,
        {
          method: "PATCH",
          headers: getHeaders(authCtx.csrfToken),
          body: JSON.stringify({ patient_id: searchResult.patient_id }),
        }
      );
      const result = await res.json();

      if (result.success) {
        showToast({ title: "Success", text: "Patient updated successfully.", type: "success" });
        onSuccess(reviewId);
        onClose();
      } else {
        showToast({
          title: "Update Failed",
          text: "Unable to update patient from this pending review. Please try again or contact support.",
          type: "error",
        });
      }
    } catch {
      showToast({
        title: "Update Failed",
        text: "Unable to update patient from this pending review. Please try again or contact support.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  if (step === "search") {
    return (
      <GeneralModal
        open={open}
        title={<span style={{ fontWeight: 600 }}>Update Existing Patient</span>}
        closeCallback={handleClose}
        successCallback={handleSearch}
        confirmButtonText="Search Patient"
        cancelButtonText="Cancel"
        isLoading={loading}
        width="430px"
      >
        <Box sx={{ pt: 1, pb: 1 }}>
          {candidates.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Typography color="#666666" sx={{ mb: 1, fontSize: "14px" }}>
                The matcher flagged {candidates.length > 1 ? "these potential existing patients" : "this potential existing patient"} for comparison against the inbound eligibility record:
              </Typography>
              <Stack spacing={1}>
                {candidates.map((c) => (
                  <Box
                    key={c.patient_id}
                    sx={{
                      border: "1px solid #e0dcda",
                      borderRadius: "6px",
                      p: 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <p style={valueStyle}>{c.name || "—"}</p>
                      <p style={labelStyle}>
                        MRN: {c.mrn || "—"} &nbsp;•&nbsp; DOB: {c.date_of_birth || "—"}
                        {c.elevated && (
                          <Chip
                            label="Elevated from low confidence"
                            size="small"
                            sx={{ ml: 1, height: "18px", fontSize: "10px" }}
                          />
                        )}
                      </p>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={loading}
                      onClick={() => handleSelectCandidate(c)}
                    >
                      Compare &amp; Select
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          <Typography color="#666666" sx={{ mb: 2, fontSize: "14px" }}>
            {candidates.length > 0
              ? "Or, search by MRN if none of the above are a match:"
              : "Enter the MRN for the existing Starfield patient this eligibility record should update."}
          </Typography>
          <InputLabel htmlFor="update_mrn_input" className="field-label" style={{ marginBottom: 4 }}>
            MRN
          </InputLabel>
          <TextField
            id="update_mrn_input"
            size="small"
            fullWidth
            value={mrn}
            placeholder="Enter MRN"
            disabled={loading}
            onChange={(e) => setMrn(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            sx={{
              backgroundColor: "#efe9e7",
              borderRadius: "5px",
              "& fieldset": { border: "none" },
            }}
          />
        </Box>
      </GeneralModal>
    );
  }


  if (step === "found" && searchResult) {
    return (
      <GeneralModal
        open={open}
        title={<span style={{ fontWeight: 600 }}>Existing Patient Found</span>}
        closeCallback={handleClose}
        successCallback={handleUpdate}
        confirmButtonText="Update Patient"
        cancelButtonText="Cancel"
        isLoading={loading}
        width="430px"
      >
        <Box sx={{ pt: 1, pb: 1 }}>
          <Typography color="#666666" sx={{ mb: 2, fontSize: "14px" }}>
            Review the inbound eligibility record and the selected Starfield patient below.
            Confirm this eligibility record should update the selected patient.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px", mb: 2 }}>
            <Box>
              <p style={labelStyle}>MRN</p>
              <p style={valueStyle}>{searchResult.mrn}</p>
            </Box>
            <Box>
              <p style={labelStyle}>Name</p>
              <p style={valueStyle}>{searchResult.full_name}</p>
            </Box>
            <Box>
              <p style={labelStyle}>DOB</p>
              <p style={valueStyle}>{searchResult.date_of_birth || "—"}</p>
            </Box>
          </Box>

          <Link
            href={`/patient_reports/${searchResult.patient_id}/action_center`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              color: "#1976d2",
            }}
          >
            View patient&apos;s profile <OpenInNewIcon sx={{ fontSize: 13 }} />
          </Link>
        </Box>
      </GeneralModal>
    );
  }



  return (
    <GeneralModal
      open={open}
      title={<span style={{ fontWeight: 600 }}>No Existing Patient Found</span>}
      closeCallback={handleClose}
      successCallback={handleGoBack}
      confirmButtonText="Go Back"
      showCancelButton={false}
      isLoading={loading}
      width="430px"
    >
      <Box sx={{ pt: 1, pb: 1 }}>
        <Typography color="#666666" sx={{ fontSize: "14px" }}>
          We couldn&apos;t find a Starfield patient with the MRN:{" "}
          <strong>{searchedMrn}</strong>. Please try again.
        </Typography>
      </Box>
    </GeneralModal>
  );
};

export default UpdatePendingReviewModal;
